import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { LOSS_TYPE_LABEL, LOSS_TYPES, type AgentSummary, type CoveragePolicy, type LossType, type Quote, type ScenarioCatalogItem } from "@ti/core";
import { AlertTriangle, ArrowLeft, ArrowRight, Bot, Check, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { PageHeader } from "@/components/domain/misc";
import { CoverageChip, TierBadge } from "@/components/domain/status";
import { ErrorPanel, Page } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Input, Label } from "@/components/ui/input";
import { EmptyState, Skeleton } from "@/components/ui/misc";
import { GRADUATED_SCHEDULE, MODE_LABEL } from "@/features/coverage/meta";
import { ImpactKindTag, LOSS_TYPE_SHORT, LossTypeChips, PolicyLink } from "@/features/coverage/policy-bits";
import {
  EMPTY_FORM,
  type Errors,
  LIMITS,
  type QuoteForm,
  STEPS,
  type StepId,
  derive,
  firstInvalidStep,
  isAvailable,
  lineKey,
  parseAgentsParam,
  parseMoney,
  toRequest,
  validate,
} from "@/features/coverage/quote-model";
import { q, useBindQuote, useCreateQuote } from "@/lib/api";
import { usePackName } from "@/lib/packs";
import { fmtDate, fmtMoney, fmtPct } from "@/lib/format";
import { cn } from "@/lib/utils";

interface QuoteSearch {
  /** Comma-separated agent IDs, e.g. `agt_refund,agt_concierge` (deep-linkable "Insure this agent"). */
  agents?: string;
  step?: StepId;
}

export const Route = createFileRoute("/_app/coverage/quote")({
  validateSearch: (s: Record<string, unknown>): QuoteSearch => ({
    agents: typeof s.agents === "string" && s.agents ? s.agents : undefined,
    step: typeof s.step === "string" && STEPS.some((x) => x.id === s.step) ? (s.step as StepId) : undefined,
  }),
  component: QuoteBuilderPage,
});

const STEP_INDEX = Object.fromEntries(STEPS.map((s, i) => [s.id, i])) as Record<StepId, number>;

function QuoteBuilderPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const agents = useQuery(q.agents());
  const policies = useQuery(q.policies());
  const allCatalog = useQuery(q.scenarioCatalog([]));

  const requested = useMemo(() => parseAgentsParam(search.agents), [search.agents]);
  const agentIds = useMemo(() => (agents.data ? requested.filter((id) => agents.data.some((a) => a.id === id)) : requested).sort(), [requested, agents.data]);
  const catalog = useQuery({ ...q.scenarioCatalog(agentIds), enabled: agentIds.length > 0 });

  const [form, setForm] = useState<QuoteForm>(EMPTY_FORM);
  const [nameTouched, setNameTouched] = useState(false);
  const [attempted, setAttempted] = useState<Set<StepId>>(new Set());
  const seeded = useRef(new Set<string>());

  // Pre-select what isn't insured yet the first time an agent's catalog arrives.
  useEffect(() => {
    if (!catalog.data) return;
    const fresh = catalog.data.filter((c) => !seeded.current.has(c.agentId));
    if (!fresh.length) return;
    for (const c of fresh) seeded.current.add(c.agentId);
    setForm((f) => {
      const selection = { ...f.selection };
      for (const c of fresh) {
        const k = lineKey(c.agentId, c.key);
        if (!selection[k]) selection[k] = { on: isAvailable(c), subLimit: c.suggestedSubLimit.amount.toLocaleString("en-US") };
      }
      return { ...f, selection };
    });
  }, [catalog.data]);

  // Default the policy name from the selected agents until the user edits it.
  const agentById = useMemo(() => new Map((agents.data ?? []).map((a) => [a.id, a])), [agents.data]);
  useEffect(() => {
    if (nameTouched) return;
    const names = agentIds.map((id) => agentById.get(id)?.name).filter(Boolean) as string[];
    const name = names.length === 0 ? "" : names.length <= 2 ? `${names.join(" & ")} cover` : `${names.length}-agent cover`;
    setForm((f) => (f.name === name ? f : { ...f, name }));
  }, [agentIds, agentById, nameTouched]);

  const d = useMemo(() => derive(form, agentIds, catalog.data), [form, agentIds, catalog.data]);
  const errors = useMemo(() => validate(form, d), [form, d]);
  const blocker = firstInvalidStep(errors);
  const step: StepId = search.step ?? (requested.length ? "scenarios" : "agents");
  const maxReachable = blocker ? STEP_INDEX[blocker] : STEPS.length - 1;

  const goto = (s: StepId) => void navigate({ search: (prev: QuoteSearch) => ({ ...prev, step: s }) });
  const next = () => {
    setAttempted((a) => new Set(a).add(step));
    if (blocker && STEP_INDEX[blocker] <= STEP_INDEX[step]) {
      requestAnimationFrame(() => document.querySelector<HTMLElement>("[aria-invalid='true']")?.focus());
      return;
    }
    const n = STEPS[STEP_INDEX[step] + 1];
    if (n) goto(n.id);
  };
  const back = () => {
    const p = STEPS[STEP_INDEX[step] - 1];
    if (p) goto(p.id);
  };

  // If someone deep-links past an invalid step, send them back to it once data has loaded.
  const ready = !agents.isPending && (agentIds.length === 0 || !catalog.isPending);
  // Checked once, after seeding and name defaults have settled (they land in the same commit as `ready`).
  const checkedDeepLink = useRef(false);
  const reachRef = useRef(maxReachable);
  reachRef.current = maxReachable;
  useEffect(() => {
    if (!ready || checkedDeepLink.current) return;
    const t = setTimeout(() => {
      checkedDeepLink.current = true;
      if (STEP_INDEX[step] > reachRef.current) goto(STEPS[reachRef.current]!.id);
    }, 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, step]);

  const setAgents = (ids: string[]) => void navigate({ search: (prev: QuoteSearch) => ({ ...prev, agents: ids.length ? [...ids].sort().join(",") : undefined }), replace: true });
  const show = (s: StepId) => attempted.has(s);

  return (
    <Page>
      <PageHeader
        breadcrumb={
          <nav aria-label="Breadcrumb">
            <Link to="/coverage" className="hover:text-fg">
              Policies & coverage
            </Link>
            <span className="px-1.5">/</span>
            <span className="text-fg-2">New policy</span>
          </nav>
        }
        title="New policy"
        description="Quote cover over your agents: pick agents, choose scenarios from their catalog, set limits and terms, then bind. One active policy per agent-scenario, so scenarios another policy covers can't be added."
        actions={
          <Button variant="ghost" size="sm" asChild>
            <Link to="/coverage">Cancel</Link>
          </Button>
        }
      />

      <Stepper current={step} maxReachable={maxReachable} onSelect={goto} />

      <div className="mt-4 grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-4">
          {agents.error ? (
            <ErrorPanel error={agents.error} reset={() => void agents.refetch()} />
          ) : step === "agents" ? (
            <AgentsStep
              form={form}
              setName={(name) => {
                setNameTouched(true);
                setForm((f) => ({ ...f, name }));
              }}
              agents={agents.data}
              selected={agentIds}
              onToggle={(id) => setAgents(agentIds.includes(id) ? agentIds.filter((x) => x !== id) : [...agentIds, id])}
              catalog={allCatalog.data}
              policies={policies.data}
              errors={show("agents") ? errors : {}}
            />
          ) : step === "scenarios" ? (
            <ScenariosStep
              agentIds={agentIds}
              agents={agentById}
              catalog={catalog}
              policies={policies.data}
              form={form}
              setForm={setForm}
              errors={show("scenarios") ? errors : { lines: errors.lines }}
            />
          ) : step === "terms" ? (
            <TermsStep form={form} setForm={setForm} d={d} errors={show("terms") ? errors : pick(errors, ["aggregate", "perClaim", "deductible"], form)} />
          ) : (
            <ReviewStep form={form} d={d} valid={!blocker} agents={agentById} />
          )}

          {step !== "review" ? (
            <div className="flex items-center justify-between gap-3">
              <Button variant="ghost" onClick={back} disabled={step === "agents"}>
                <ArrowLeft /> Back
              </Button>
              <div className="flex items-center gap-3">
                {show(step) && blocker && STEP_INDEX[blocker] <= STEP_INDEX[step] ? (
                  <span role="alert" className="text-xs text-critical-text">
                    Fix the highlighted fields to continue.
                  </span>
                ) : null}
                <Button onClick={next}>
                  {step === "terms" ? "Get quote" : "Continue"} <ArrowRight />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <Button variant="ghost" onClick={back}>
                <ArrowLeft /> Back to limits & terms
              </Button>
            </div>
          )}
        </div>

        <Summary form={form} d={d} agents={agentById} step={step} />
      </div>
    </Page>
  );
}

/** Terms errors are shown live once the user edits a value away from the suggestion. */
function pick(e: Errors, keys: Array<"aggregate" | "perClaim" | "deductible">, form: QuoteForm): Errors {
  const out: Errors = {};
  for (const k of keys) {
    const edited = k === "aggregate" ? form.aggregate !== null : k === "perClaim" ? form.perClaim !== null : form.deductible !== EMPTY_FORM.deductible;
    if (edited && e[k]) out[k] = e[k];
  }
  return out;
}

// ─── Stepper ─────────────────────────────────────────────────────────────────

function Stepper({ current, maxReachable, onSelect }: { current: StepId; maxReachable: number; onSelect: (s: StepId) => void }) {
  const ci = STEP_INDEX[current];
  return (
    <ol className="grid grid-cols-4 gap-2" aria-label="Quote steps">
      {STEPS.map((s, i) => {
        const done = i < ci;
        const active = i === ci;
        const reachable = i <= maxReachable;
        return (
          <li key={s.id}>
            <button
              type="button"
              onClick={() => onSelect(s.id)}
              disabled={!reachable && !active}
              aria-current={active ? "step" : undefined}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60",
                active ? "border-accent/50 bg-accent-soft/40" : "bg-surface hover:bg-surface-2",
              )}
            >
              <span
                className={cn(
                  "flex size-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold tabular",
                  active ? "border-accent text-accent" : done ? "border-fg-3 bg-surface-3 text-fg" : "border-border-strong text-fg-3",
                )}
              >
                {done ? <Check className="size-3.5" aria-label="completed" /> : i + 1}
              </span>
              <span className="min-w-0">
                <span className={cn("block text-[13px] font-medium", active ? "text-fg" : "text-fg-2")}>{s.label}</span>
                <span className="block truncate text-[11px] text-fg-3">{s.hint}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

// ─── Shared form bits ────────────────────────────────────────────────────────

function FieldError({ id, error, hint }: { id: string; error?: string; hint?: ReactNode }) {
  if (error)
    return (
      <p id={`${id}-error`} role="alert" className="mt-1 text-[11.5px] text-critical-text">
        {error}
      </p>
    );
  return hint ? (
    <p id={`${id}-hint`} className="mt-1 text-[11px] text-fg-3">
      {hint}
    </p>
  ) : null;
}

function MoneyInput({ id, value, onChange, error, hint, className, label }: { id: string; value: string; onChange: (v: string) => void; error?: string; hint?: boolean; className?: string; label?: string }) {
  return (
    <div className={cn("relative", className)}>
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] text-fg-3" aria-hidden>
        $
      </span>
      <Input
        id={id}
        aria-label={label}
        inputMode="numeric"
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          const n = parseMoney(value);
          if (Number.isFinite(n)) onChange(n.toLocaleString("en-US"));
        }}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        className={cn("pl-6 text-right tabular", error && "border-critical/60")}
      />
    </div>
  );
}

function platformLabel(a: AgentSummary) {
  return a.platform ? `${a.platform.name} platform` : a.framework ? `In-house · ${a.framework}` : "In-house";
}

// ─── Step 1: agents ──────────────────────────────────────────────────────────

function AgentsStep({
  form,
  setName,
  agents,
  selected,
  onToggle,
  catalog,
  policies,
  errors,
}: {
  form: QuoteForm;
  setName: (v: string) => void;
  agents: AgentSummary[] | undefined;
  selected: string[];
  onToggle: (id: string) => void;
  catalog: ScenarioCatalogItem[] | undefined;
  policies: CoveragePolicy[] | undefined;
  errors: Errors;
}) {
  const uid = useId();
  const policyName = new Map((policies ?? []).map((p) => [p.id, p.name]));
  return (
    <Card>
      <CardHeader title="Name and agents" description="A policy covers a chosen set of your agents. You can hold several policies, e.g. one per line of business or carrier." />
      <CardBody className="flex flex-col gap-5">
        <div className="max-w-md">
          <Label htmlFor={`${uid}-name`}>Policy name</Label>
          <Input
            id={`${uid}-name`}
            value={form.name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Engineering & Infrastructure"
            maxLength={LIMITS.name.max}
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={errors.name ? `${uid}-name-error` : `${uid}-name-hint`}
          />
          <FieldError id={`${uid}-name`} error={errors.name} hint="Shown on the policy, endorsements and your protection page." />
        </div>

        <fieldset>
          <legend className="mb-2 text-xs font-medium text-fg-2">Agents to insure</legend>
          {!agents ? (
            <div className="grid gap-2 md:grid-cols-2">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="h-[104px]" />
              ))}
            </div>
          ) : (
            <div className="grid gap-2 md:grid-cols-2">
              {agents.map((a) => {
                const on = selected.includes(a.id);
                const items = (catalog ?? []).filter((c) => c.agentId === a.id);
                const available = items.filter(isAvailable).length;
                const ineligible = items.filter((c) => !c.eligible).length;
                const covered = items.filter((c) => c.coveredByPolicyId).length;
                return (
                  <label
                    key={a.id}
                    className={cn(
                      "flex cursor-pointer gap-3 rounded-lg border p-3 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
                      on ? "border-accent/50 bg-accent-soft/30" : "hover:bg-surface-2",
                    )}
                  >
                    <input type="checkbox" checked={on} onChange={() => onToggle(a.id)} className="mt-0.5 size-4 shrink-0 cursor-pointer accent-accent" />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <Bot className="size-3.5 text-fg-3" aria-hidden />
                        <span className="font-mono text-[13px] font-medium text-fg">{a.name}</span>
                        <TierBadge tier={a.tier} />
                        <span className="text-xs text-fg-3 tabular">ARS {a.ars}</span>
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-fg-3">
                        {platformLabel(a)} · {a.model}
                      </span>
                      <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-fg-2">
                        <span>
                          Insured <span className="font-medium tabular text-fg">{fmtMoney(a.insuredLimit, { compact: true })}</span>
                          {a.policyIds.length ? <span className="text-fg-3"> · {a.policyIds.map((id) => policyName.get(id) ?? id).join(", ")}</span> : <span className="text-fg-3"> · no policy</span>}
                        </span>
                      </span>
                      <span className="mt-1 block text-[11.5px] text-fg-3">
                        {catalog ? (
                          <>
                            <span className={cn("font-medium tabular", available ? "text-fg" : "text-fg-3")}>{available}</span> available to insure · {covered} already covered
                            {ineligible ? ` · ${ineligible} not eligible yet` : ""}
                          </>
                        ) : (
                          "Loading catalog…"
                        )}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          {errors.agents ? (
            <p role="alert" className="mt-2 text-[11.5px] text-critical-text">
              {errors.agents}
            </p>
          ) : null}
        </fieldset>
      </CardBody>
    </Card>
  );
}

// ─── Step 2: scenarios ───────────────────────────────────────────────────────

type LossGroup = "third_party" | "both" | "first_party";
const lossGroup = (c: ScenarioCatalogItem): LossGroup =>
  c.lossTypes.includes("first_party") && c.lossTypes.includes("third_party") ? "both" : c.lossTypes.includes("first_party") ? "first_party" : "third_party";
const GROUP_ORDER: Record<LossGroup, number> = { third_party: 0, both: 1, first_party: 2 };
const GROUP_LABEL: Record<LossGroup, string> = {
  third_party: "Third-party · people your agents act for",
  both: "First- and third-party",
  first_party: "First-party · your own losses",
};

function ScenariosStep({
  agentIds,
  agents,
  catalog,
  policies,
  form,
  setForm,
  errors,
}: {
  agentIds: string[];
  agents: Map<string, AgentSummary>;
  catalog: { data: ScenarioCatalogItem[] | undefined; isPending: boolean; error: unknown; refetch: () => unknown };
  policies: CoveragePolicy[] | undefined;
  form: QuoteForm;
  setForm: (fn: (f: QuoteForm) => QuoteForm) => void;
  errors: Errors;
}) {
  const policyName = new Map((policies ?? []).map((p) => [p.id, p.name]));
  const packName = usePackName();
  const [lossFilter, setLossFilter] = useState<LossType | "all">("all");
  const scoped = (catalog.data ?? []).filter((c) => agentIds.includes(c.agentId));
  const lossCount = (t: LossType) => scoped.filter((c) => c.lossTypes.includes(t)).length;
  const setLine = (k: string, patch: Partial<{ on: boolean; subLimit: string }>) =>
    setForm((f) => ({ ...f, selection: { ...f.selection, [k]: { on: false, subLimit: "", ...f.selection[k], ...patch } } }));

  if (!agentIds.length)
    return (
      <Card>
        <EmptyState icon={<Bot />} title="No agents selected" description="Go back and select at least one agent to see its scenario catalog." />
      </Card>
    );
  if (catalog.error) return <ErrorPanel error={catalog.error} reset={() => void catalog.refetch()} />;

  return (
    <>
      {errors.scenarios ? (
        <div role="alert" className="flex items-center gap-2 rounded-md border border-critical/40 bg-critical/10 px-3 py-2 text-xs text-critical-text">
          <AlertTriangle className="size-3.5" aria-hidden /> {errors.scenarios}
        </div>
      ) : null}
      <div role="group" aria-label="Filter scenarios by loss type" className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-fg-3">Loss type</span>
        {(["all", ...LOSS_TYPES] as const).map((t) => (
          <button
            key={t}
            type="button"
            aria-pressed={lossFilter === t}
            onClick={() => setLossFilter(t)}
            title={t === "all" ? undefined : LOSS_TYPE_LABEL[t]}
            className={cn(
              "inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors",
              lossFilter === t ? "border-accent/50 bg-accent-soft text-accent" : "border-border-strong text-fg-2 hover:bg-surface-2 hover:text-fg",
            )}
          >
            {t === "all" ? "All" : LOSS_TYPE_SHORT[t]}
            <span className="text-fg-3 tabular">{t === "all" ? scoped.length : lossCount(t)}</span>
          </button>
        ))}
        <span className="ml-2 text-[11px] text-fg-3">First-party covers your own losses; third-party covers people your agents act for.</span>
      </div>
      {agentIds.map((id) => {
        const a = agents.get(id);
        // Grouped by loss type (third-party, both, first-party); within a group available first, then already covered, then not eligible.
        const rank = (c: ScenarioCatalogItem) => (isAvailable(c) ? 0 : c.coveredByPolicyId ? 1 : 2);
        const items = (catalog.data ?? [])
          .filter((c) => c.agentId === id && (lossFilter === "all" || c.lossTypes.includes(lossFilter)))
          .sort((x, y) => GROUP_ORDER[lossGroup(x)] - GROUP_ORDER[lossGroup(y)] || rank(x) - rank(y));
        const groups = new Set(items.map(lossGroup));
        const available = items.filter(isAvailable);
        const allOn = available.length > 0 && available.every((c) => form.selection[lineKey(c.agentId, c.key)]?.on);
        return (
          <Card key={id}>
            <CardHeader
              title={
                <span className="flex flex-wrap items-center gap-2">
                  <Bot className="size-3.5 text-fg-3" aria-hidden />
                  <span className="font-mono">{a?.name ?? id}</span>
                  {a ? <TierBadge tier={a.tier} /> : null}
                  {a ? <span className="text-xs font-normal text-fg-3 tabular">ARS {a.ars}</span> : null}
                </span>
              }
              description={a ? `${platformLabel(a)} · rates reflect this agent's current tier and each scenario's state` : undefined}
              actions={
                available.length ? (
                  <Button variant="ghost" size="sm" onClick={() => available.forEach((c) => setLine(lineKey(c.agentId, c.key), { on: !allOn }))}>
                    {allOn ? "Clear all" : `Select all ${available.length} available`}
                  </Button>
                ) : null
              }
            />
            {catalog.isPending ? (
              <CardBody>
                <Skeleton className="h-40" />
              </CardBody>
            ) : !items.length ? (
              lossFilter !== "all" ? (
                <EmptyState className="py-8" title={`No ${LOSS_TYPE_SHORT[lossFilter].toLowerCase()} scenarios for this agent`} description="Clear the loss-type filter to see its other scenarios." />
              ) : (
                <EmptyState className="py-8" title="No insurable scenarios" description="Scenarios appear once the agent's action classes are observed and classified." />
              )
            ) : (
              <div className="scrollbar-thin overflow-x-auto">
                <table className="w-full text-[13px]">
                  <caption className="sr-only">Scenario catalog for {a?.name ?? id}</caption>
                  <thead>
                    <tr className="border-b text-[11px] uppercase tracking-wide text-fg-3">
                      <th scope="col" className="w-10 px-3 py-2">
                        <span className="sr-only">Include</span>
                      </th>
                      <th scope="col" className="px-2 py-2 text-left font-medium">
                        Scenario
                      </th>
                      <th scope="col" className="px-2 py-2 text-left font-medium">
                        State
                      </th>
                      <th scope="col" className="whitespace-nowrap px-2 py-2 text-right font-medium">
                        Rate / $1k
                      </th>
                      <th scope="col" className="px-2 py-2 text-right font-medium">
                        Sub-limit
                      </th>
                      <th scope="col" className="whitespace-nowrap px-3 py-2 text-right font-medium">
                        Est. premium
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c, i) => {
                      const k = lineKey(c.agentId, c.key);
                      const group = lossGroup(c);
                      const groupHeader =
                        groups.size > 1 && (i === 0 || lossGroup(items[i - 1]!) !== group) ? (
                          <tr key={`${k}-group`} className="border-b bg-surface-2/60">
                            <th scope="rowgroup" colSpan={6} className="px-3 py-1.5 text-left text-[11px] font-medium uppercase tracking-wide text-fg-3">
                              {GROUP_LABEL[group]}
                            </th>
                          </tr>
                        ) : null;
                      const sel = form.selection[k];
                      const avail = isAvailable(c);
                      const on = avail && Boolean(sel?.on);
                      const sub = parseMoney(sel?.subLimit ?? "");
                      const err = on ? errors.lines?.[k] : undefined;
                      const inputId = `sl-${k.replace(/[^a-z0-9]/gi, "-")}`;
                      return [
                        groupHeader,
                        <tr key={k} className={cn("border-b align-top last:border-0", !avail && "bg-surface-2/40", on && "bg-accent-soft/20")}>
                          <td className="px-3 py-2.5">
                            <input
                              type="checkbox"
                              checked={on}
                              disabled={!avail}
                              onChange={(e) => setLine(k, { on: e.target.checked })}
                              aria-label={`Include ${c.label}`}
                              className="mt-0.5 size-4 cursor-pointer accent-accent disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="px-2 py-2.5">
                            <div className={cn("font-medium", avail ? "text-fg" : "text-fg-2")}>{c.label}</div>
                            <div className="font-mono text-[11.5px] text-fg-3">{c.key}</div>
                            <div className="text-xs text-fg-3">{c.envelope}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                              <LossTypeChips types={c.lossTypes} />
                              <ImpactKindTag kind={c.impactKind} />
                              <span className="text-[11px] text-fg-3">· {c.pack ? `${packName(c.pack)} pack` : "Core ontology"}</span>
                            </div>
                            {c.coveredByPolicyId ? (
                              <div className="mt-1 flex flex-wrap items-center gap-1 text-[11.5px] text-fg-2">
                                <ShieldCheck className="size-3.5 text-fg-3" aria-hidden /> Already covered by
                                <PolicyLink id={c.coveredByPolicyId} name={policyName.get(c.coveredByPolicyId) ?? c.coveredByPolicyId} className="text-[11.5px]" />
                              </div>
                            ) : !c.eligible ? (
                              <div className="mt-1 flex items-start gap-1 text-[11.5px] text-fg-2">
                                <AlertTriangle className="mt-px size-3.5 shrink-0 text-fg-3" aria-hidden /> Not eligible: {c.ineligibleReason ?? "not insurable in its current state"}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-2 py-2.5">
                            <CoverageChip state={c.state} />
                          </td>
                          <td className="px-2 py-2.5 text-right tabular text-fg-2">{fmtMoney(c.ratePer1k, { cents: true })}</td>
                          <td className="px-2 py-2.5">
                            {on ? (
                              <div className="ml-auto w-32">
                                <MoneyInput id={inputId} label={`Sub-limit for ${c.label}`} value={sel?.subLimit ?? ""} onChange={(v) => setLine(k, { subLimit: v })} error={err} />
                                <FieldError id={inputId} error={err} />
                              </div>
                            ) : (
                              <div className="text-right text-xs text-fg-3 tabular">{avail ? `Suggested ${fmtMoney(c.suggestedSubLimit, { compact: true })}` : "—"}</div>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular">
                            {on && Number.isFinite(sub) ? fmtMoney((c.ratePer1k * sub) / 1000, { cents: true }) : <span className="text-fg-3">—</span>}
                          </td>
                        </tr>,
                      ];
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      })}
      <p className="text-[11px] text-fg-3">
        Estimates are rate × sub-limit before policy-level factors (deductible, terms, aggregate cap) and the carrier minimum premium. The binding price comes from the quote in the last
        step.
      </p>
    </>
  );
}

// ─── Step 3: limits & terms ──────────────────────────────────────────────────

const AGG_PICKS = [25_000, 50_000, 100_000, 250_000, 500_000, 1_000_000];
const PER_CLAIM_PICKS = [5_000, 10_000, 25_000, 50_000];
const DEDUCTIBLE_PICKS = [0, 500, 1_000, 2_500, 5_000];

function Picks({ values, current, onPick, label }: { values: number[]; current: number; onPick: (n: number) => void; label: string }) {
  return (
    <div role="group" aria-label={label} className="mt-1.5 flex flex-wrap gap-1">
      {values.map((v) => (
        <button
          key={v}
          type="button"
          aria-pressed={current === v}
          onClick={() => onPick(v)}
          className={cn(
            "h-6 cursor-pointer rounded border px-1.5 text-[11px] tabular transition-colors",
            current === v ? "border-accent/50 bg-accent-soft text-fg" : "text-fg-3 hover:bg-surface-2 hover:text-fg",
          )}
        >
          {v === 0 ? "$0" : fmtMoney(v, { compact: true }).replace(".0", "")}
        </button>
      ))}
    </div>
  );
}

function TermsStep({ form, setForm, d, errors }: { form: QuoteForm; setForm: (fn: (f: QuoteForm) => QuoteForm) => void; d: ReturnType<typeof derive>; errors: Errors }) {
  const uid = useId();
  const ids = { agg: `${uid}-agg`, per: `${uid}-per`, ded: `${uid}-ded` };
  const sum = d.lines.reduce((n, l) => n + (Number.isFinite(l.subLimit) ? l.subLimit : 0), 0);
  const maxSub = Math.max(0, ...d.lines.map((l) => l.subLimit).filter(Number.isFinite));
  const fmt = (n: number) => n.toLocaleString("en-US");

  return (
    <>
      <Card>
        <CardHeader title="Limits" description="The aggregate limit caps everything the policy pays in the year; scenario sub-limits and the per-claim limit apply within it." />
        <CardBody className="grid gap-5 md:grid-cols-3">
          <div>
            <Label htmlFor={ids.agg}>Aggregate limit</Label>
            <MoneyInput id={ids.agg} value={d.aggregateRaw} onChange={(v) => setForm((f) => ({ ...f, aggregate: v }))} error={errors.aggregate} hint />
            <FieldError
              id={ids.agg}
              error={errors.aggregate}
              hint={
                form.aggregate === null
                  ? `Suggested from ${d.lines.length} sub-limits totalling ${fmtMoney(sum)}. Minimum ${fmtMoney(LIMITS.aggregate.min)}.`
                  : `Sub-limits total ${fmtMoney(sum)}. Minimum ${fmtMoney(LIMITS.aggregate.min)}.`
              }
            />
            <Picks label="Aggregate limit presets" values={AGG_PICKS.filter((v) => v >= maxSub)} current={d.aggregate} onPick={(v) => setForm((f) => ({ ...f, aggregate: fmt(v) }))} />
          </div>
          <div>
            <Label htmlFor={ids.per}>Per-claim limit</Label>
            <MoneyInput id={ids.per} value={d.perClaimRaw} onChange={(v) => setForm((f) => ({ ...f, perClaim: v }))} error={errors.perClaim} hint />
            <FieldError id={ids.per} error={errors.perClaim} hint={`Most the policy pays on one claim. Minimum ${fmtMoney(LIMITS.perClaim.min)}.`} />
            <Picks label="Per-claim limit presets" values={PER_CLAIM_PICKS} current={d.perClaim} onPick={(v) => setForm((f) => ({ ...f, perClaim: fmt(v) }))} />
          </div>
          <div>
            <Label htmlFor={ids.ded}>Deductible</Label>
            <MoneyInput id={ids.ded} value={form.deductible} onChange={(v) => setForm((f) => ({ ...f, deductible: v }))} error={errors.deductible} hint />
            <FieldError id={ids.ded} error={errors.deductible} hint="Per claim, borne by you. A higher deductible lowers the premium." />
            <Picks label="Deductible presets" values={DEDUCTIBLE_PICKS} current={d.deductible} onPick={(v) => setForm((f) => ({ ...f, deductible: fmt(v) }))} />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Terms" description="What happens to a scenario's cover when an issue on it isn't fixed within its grace window. During grace, cover continues either way." />
        <CardBody>
          <fieldset className="grid gap-3 md:grid-cols-2">
            <legend className="sr-only">Policy terms</legend>
            {(["graduated", "strict"] as const).map((m) => (
              <label
                key={m}
                className={cn(
                  "flex cursor-pointer gap-3 rounded-lg border p-3 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
                  form.mode === m ? "border-accent/50 bg-accent-soft/30" : "hover:bg-surface-2",
                )}
              >
                <input type="radio" name="mode" value={m} checked={form.mode === m} onChange={() => setForm((f) => ({ ...f, mode: m }))} className="mt-0.5 size-4 shrink-0 cursor-pointer accent-accent" />
                <span className="min-w-0">
                  <span className="flex items-center gap-2 text-[13px] font-medium text-fg">
                    {MODE_LABEL[m].label}
                    {m === "graduated" ? <Badge tone="neutral">Recommended</Badge> : <Badge tone="neutral">Lower premium</Badge>}
                  </span>
                  <span className="mt-0.5 block text-xs text-fg-3">{MODE_LABEL[m].help}</span>
                  {m === "graduated" ? (
                    <span className="mt-2 grid grid-cols-[auto_auto_auto] gap-x-3 gap-y-0.5 text-[11px] text-fg-3 tabular">
                      {GRADUATED_SCHEDULE.slice(1).map((r) => (
                        <span key={r.id} className="contents">
                          <span>{r.window}</span>
                          <span className="text-right text-fg-2">{r.coverage === null ? "Excluded" : `${fmtPct(r.coverage)} limit`}</span>
                          <span className="text-right">{r.coinsurance === null ? "" : `${fmtPct(r.coinsurance)} co-ins.`}</span>
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span className="mt-2 block text-[11px] text-fg-3">Excluded from the grace deadline until the fix is verified. Simpler, but a missed deadline removes cover in one step.</span>
                  )}
                </span>
              </label>
            ))}
          </fieldset>
          <p className="mt-3 text-[11px] text-fg-3">Term: 12 months from binding. Grace windows: critical 48 h (covered at 50%), high 7 days, medium 14 days, low 30 days.</p>
        </CardBody>
      </Card>
    </>
  );
}

// ─── Step 4: review & bind ───────────────────────────────────────────────────

function ReviewStep({ form, d, valid, agents }: { form: QuoteForm; d: ReturnType<typeof derive>; valid: boolean; agents: Map<string, AgentSummary> }) {
  const navigate = useNavigate();
  const create = useCreateQuote();
  const bind = useBindQuote();
  const [result, setResult] = useState<{ key: string; quote: Quote } | null>(null);
  const body = valid ? toRequest(form, d) : null;
  const key = body ? JSON.stringify(body) : "";
  const latest = useRef(key);
  latest.current = key;

  const request = () => {
    if (!body) return;
    const k = key;
    create.mutate(body, { onSuccess: (quote) => latest.current === k && setResult({ key: k, quote }) });
  };

  // Re-quote (debounced) whenever the request changes while on this step.
  useEffect(() => {
    if (!key || result?.key === key) return;
    const t = setTimeout(request, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const quote = result?.quote;
  const stale = Boolean(result && result.key !== key);

  if (!valid)
    return (
      <Card>
        <EmptyState icon={<AlertTriangle />} title="The quote request is incomplete" description="Go back and fix the highlighted fields to get a quote." />
      </Card>
    );

  return (
    <>
      <Card>
        <CardHeader
          title="Quote"
          description={quote ? `${quote.carrier} · quote ${quote.id} · valid until ${fmtDate(quote.expiresAt)}` : "Pricing your selection with the carrier's rating plan…"}
          actions={
            <Button variant="ghost" size="sm" onClick={request} disabled={create.isPending}>
              {create.isPending ? <Loader2 className="animate-spin" /> : <RefreshCw />} Refresh
            </Button>
          }
        />
        <CardBody className="flex flex-col gap-4">
          {create.isError && !quote ? (
            <ErrorPanel error={create.error} reset={request} />
          ) : !quote ? (
            <Skeleton className="h-24" />
          ) : (
            <div className={cn("flex flex-wrap items-end justify-between gap-4 transition-opacity", (stale || create.isPending) && "opacity-60")} aria-busy={create.isPending}>
              <div>
                <div className="text-xs font-medium text-fg-3">Annual premium</div>
                <div className="mt-1 flex items-baseline gap-3">
                  <span className="text-3xl font-semibold tracking-tight tabular" aria-live="polite">
                    {fmtMoney(quote.premium.annual, { cents: true })}
                  </span>
                  <span className="text-sm text-fg-2 tabular">{fmtMoney(quote.premium.monthly, { cents: true })} / month</span>
                </div>
                <div className="mt-1 text-xs text-fg-3 tabular">
                  {minimumApplied(quote) ? "Carrier minimum premium applies · " : ""}
                  {fmtPct(quote.premium.annual.amount / quote.request.aggregateLimit, 2)} rate on line · {quote.lines.length} {quote.lines.length === 1 ? "scenario" : "scenarios"} ·{" "}
                  {fmtMoney(quote.request.aggregateLimit)} aggregate
                </div>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <Button
                  size="lg"
                  disabled={!quote.bindable || stale || create.isPending || bind.isPending}
                  onClick={() => bind.mutate(quote.id, { onSuccess: (p) => void navigate({ to: "/coverage/policies/$policyId", params: { policyId: p.id } }) })}
                >
                  {bind.isPending ? <Loader2 className="animate-spin" /> : <ShieldCheck />} Bind policy
                </Button>
                <span className="text-[11px] text-fg-3">{quote.bindable ? "Submits to the carrier; status is Pending bind until bound." : "Nothing in this quote can be bound."}</span>
              </div>
            </div>
          )}

          {quote?.warnings.length ? (
            <ul className="flex flex-col gap-1 rounded-md border border-warning/40 bg-warning/10 px-3 py-2">
              {quote.warnings.map((w) => (
                <li key={w} className="flex items-start gap-2 text-xs text-fg-2">
                  <AlertTriangle className="mt-px size-3.5 shrink-0 text-warning-text" aria-label="Warning" />
                  {w}
                </li>
              ))}
            </ul>
          ) : null}
        </CardBody>
      </Card>

      {quote ? (
        <div className="grid gap-4 2xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
          <Card>
            <CardHeader title="Lines" description="Premium per scenario at each agent's current tier, before policy-level factors." />
            <table className="w-full text-[13px]">
              <caption className="sr-only">Quoted scenarios</caption>
              <thead>
                <tr className="border-b text-[11px] uppercase tracking-wide text-fg-3">
                  <th scope="col" className="px-4 py-2 text-left font-medium">
                    Scenario
                  </th>
                  <th scope="col" className="px-2 py-2 text-left font-medium">
                    Agent
                  </th>
                  <th scope="col" className="px-2 py-2 text-right font-medium">
                    Sub-limit
                  </th>
                  <th scope="col" className="px-4 py-2 text-right font-medium">
                    Premium
                  </th>
                </tr>
              </thead>
              <tbody>
                {quote.lines.map((l) => (
                  <tr key={lineKey(l.agentId, l.key)} className="border-b last:border-0">
                    <td className="px-4 py-2">
                      <div className="text-fg">{l.label}</div>
                      <div className="font-mono text-[11.5px] text-fg-3">{l.key}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <span className="font-mono text-[12px]">{agents.get(l.agentId)?.name ?? l.agentId}</span>
                        <TierBadge tier={l.tier} showLabel={false} />
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right tabular">{fmtMoney(l.subLimit)}</td>
                    <td className="px-4 py-2 text-right tabular">{fmtMoney(l.premium, { cents: true })}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t text-xs text-fg-3">
                  <td className="px-4 py-2" colSpan={2}>
                    Lines subtotal
                  </td>
                  <td className="px-2 py-2 text-right tabular">{fmtMoney(quote.lines.reduce((n, l) => n + l.subLimit.amount, 0))}</td>
                  <td className="px-4 py-2 text-right tabular">{fmtMoney(quote.lines.reduce((n, l) => n + l.premium.amount, 0), { cents: true })}</td>
                </tr>
              </tfoot>
            </table>
          </Card>
          <Card>
            <CardHeader title="Pricing factors" description="Multiplied into the lines subtotal; a carrier minimum premium may apply." />
            <table className="w-full text-[13px]">
              <caption className="sr-only">Pricing factors</caption>
              <tbody>
                {quote.factors.map((f) => (
                  <tr key={f.label} className="border-b last:border-0">
                    <th scope="row" className="px-4 py-2 text-left font-normal">
                      <div className="text-fg">{f.label}</div>
                      <div className="text-xs text-fg-3">{f.detail}</div>
                    </th>
                    <td className="px-4 py-2 text-right font-medium tabular">× {f.value.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      ) : null}
    </>
  );
}

/** The priced lines × factors fall below the annual premium, so a minimum premium was applied. */
function minimumApplied(quote: Quote) {
  const lines = quote.lines.reduce((n, l) => n + l.premium.amount, 0);
  const factor = quote.factors.reduce((n, f) => n * f.value, 1);
  return lines * factor < quote.premium.annual.amount - 0.5;
}

// ─── Summary (aside) ─────────────────────────────────────────────────────────

function Summary({ form, d, agents, step }: { form: QuoteForm; d: ReturnType<typeof derive>; agents: Map<string, AgentSummary>; step: StepId }) {
  const sum = d.lines.reduce((n, l) => n + (Number.isFinite(l.subLimit) ? l.subLimit : 0), 0);
  const money = (n: number) => (Number.isFinite(n) ? fmtMoney(n) : "—");
  const rows: Array<[string, ReactNode]> = [
    ["Scenarios", `${d.lines.length} selected`],
    ["Sub-limits", fmtMoney(sum)],
    ["Aggregate", money(d.aggregate)],
    ["Per claim", money(d.perClaim)],
    ["Deductible", money(d.deductible)],
    ["Terms", MODE_LABEL[form.mode].label],
  ];
  return (
    <Card className="lg:sticky lg:top-4">
      <CardHeader title="Summary" description={form.name.trim() || "Untitled policy"} />
      <CardBody className="flex flex-col gap-4">
        <div>
          <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-fg-3">Agents</div>
          {d.agentIds.length ? (
            <ul className="flex flex-col gap-1">
              {d.agentIds.map((id) => {
                const a = agents.get(id);
                const n = d.lines.filter((l) => l.item.agentId === id).length;
                return (
                  <li key={id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <Bot className="size-3.5 shrink-0 text-fg-3" aria-hidden />
                      <span className="truncate font-mono text-[12px] text-fg">{a?.name ?? id}</span>
                      {a ? <TierBadge tier={a.tier} showLabel={false} /> : null}
                    </span>
                    <span className="shrink-0 text-fg-3 tabular">
                      {n} {n === 1 ? "scenario" : "scenarios"}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-xs text-fg-3">None selected</p>
          )}
        </div>
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 border-t pt-3 text-[13px]">
          {rows.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-fg-3">{k}</dt>
              <dd className="text-right tabular text-fg">{v}</dd>
            </div>
          ))}
        </dl>
        {step !== "review" ? (
          <div className="rounded-md border bg-surface-2/60 px-3 py-2.5">
            <div className="text-[11px] text-fg-3">Indicative premium</div>
            <div className="mt-0.5 text-lg font-semibold tabular">
              {fmtMoney(d.estimate, { cents: true })}
              <span className="ml-1 text-xs font-normal text-fg-3">/yr</span>
            </div>
            <p className="mt-0.5 text-[11px] text-fg-3">Catalog rate × sub-limit, before policy-level factors and the carrier minimum premium. The quote is binding.</p>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
